import { Controller } from "@hotwired/stimulus"

const MINUTES_IN_DAY = 24 * 60
const MINUTE_STEP = 15

// Keeps the start/end time range and the hours/minutes duration in sync.
//
// - Editing the start or end time fills in hours and minutes (rounded to the
//   nearest 15-minute step), with hours/minutes as the billing source of truth.
// - Editing the hours or minutes of a ranged entry moves the end time so that
//   start_time + hours + minutes = end_time.
export default class extends Controller {
  static targets = ["startTime", "endTime", "hours", "minutes", "minutesDisplay", "minutesPopover"]
  static values = { autofill: Boolean }

  connect() {
    if (this.autofillValue) this.fillCurrentTimeIfEmpty()
    this.syncMinutesDisplay()
  }

  // A minute step was picked from the popover: update the duration, mirror the
  // choice onto the trigger, and dismiss the popover.
  selectMinute() {
    this.toRange()
    this.syncMinutesDisplay()
    // Only the desktop layout opens the popover; on mobile the steps render
    // inline, so hidePopover() would throw on a popover that isn't showing.
    if (this.hasMinutesPopoverTarget && this.minutesPopoverTarget.matches(":popover-open")) {
      this.minutesPopoverTarget.hidePopover()
    }
  }

  // Prime an empty new-entry form with the current time so the user only has to
  // adjust the end. Edit forms come pre-filled, so empty fields are left alone.
  fillCurrentTimeIfEmpty() {
    const now = new Date()
    const current = this.formatTime(now.getHours() * 60 + now.getMinutes())
    if (this.hasStartTimeTarget && this.startTimeTarget.value === "") this.startTimeTarget.value = current
    if (this.hasEndTimeTarget && this.endTimeTarget.value === "") this.endTimeTarget.value = current
  }

  // End time committed (blur) -> snap it up to the next 15-minute step, then
  // re-derive. Deferring to blur lets the user type the minutes freely.
  snapEndTime() {
    this.snapTime(this.endTimeTarget, Math.ceil)
  }

  // Start time committed (blur) -> snap it down to the previous 15-minute step.
  snapStartTime() {
    this.snapTime(this.startTimeTarget, Math.floor)
  }

  snapTime(target, round) {
    const minutes = this.minutesSinceMidnight(target.value)
    if (minutes === null) return

    const rounded = (round(minutes / MINUTE_STEP) * MINUTE_STEP) % MINUTES_IN_DAY
    if (rounded === minutes) return

    target.value = this.formatTime(rounded)
    this.fromRange()
  }

  // start/end time changed -> derive hours + minutes
  fromRange() {
    const start = this.minutesSinceMidnight(this.startTimeTarget.value)
    const end = this.minutesSinceMidnight(this.endTimeTarget.value)
    if (start === null || end === null) return

    let diff = end - start
    if (diff < 0) diff += MINUTES_IN_DAY
    if (diff === 0) return

    let hours = Math.floor(diff / 60)
    let minutes = this.roundToStep(diff % 60)
    if (minutes === 60) {
      hours += 1
      minutes = 0
    }

    this.hoursTarget.value = hours
    this.selectMinutes(minutes)
    this.syncMinutesDisplay()
  }

  // hours/minutes changed on a ranged entry -> move the end time to match
  toRange() {
    const start = this.minutesSinceMidnight(this.startTimeTarget.value)
    if (start === null || this.endTimeTarget.value === "") return

    const hours = parseInt(this.hoursTarget.value, 10) || 0
    const end = (start + hours * 60 + this.selectedMinutes()) % MINUTES_IN_DAY
    this.endTimeTarget.value = this.formatTime(end)
  }

  minutesSinceMidnight(value) {
    if (!value) return null
    const [hour, minute] = value.split(":").map(Number)
    if (Number.isNaN(hour) || Number.isNaN(minute)) return null
    return hour * 60 + minute
  }

  roundToStep(minutes) {
    return Math.round(minutes / MINUTE_STEP) * MINUTE_STEP
  }

  selectMinutes(minutes) {
    this.minutesTargets.forEach((radio) => {
      radio.checked = Number(radio.value) === minutes
    })
  }

  selectedMinutes() {
    const checked = this.minutesTargets.find((radio) => radio.checked)
    return checked ? Number(checked.value) : 0
  }

  // Mirror the chosen minute step onto the popover trigger. No-op for the
  // calendar modal, which shows the full radio row and has no trigger display.
  syncMinutesDisplay() {
    if (!this.hasMinutesDisplayTarget) return
    this.minutesDisplayTarget.textContent = this.selectedMinutes()
  }

  formatTime(totalMinutes) {
    const hour = Math.floor(totalMinutes / 60)
    const minute = totalMinutes % 60
    return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
  }
}
